// src/components/webinar_funnel/sections/TableControls.jsx
import React from 'react';
import {
  MagnifyingGlassIcon,
  ChevronDownIcon,
  SwatchIcon,
  Cog6ToothIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { SCALING_TYPES } from '../../../../utils/heatMapUtils';

const TableControls = ({
  // Filter state
  filterType, setFilterType,
  dateRange, setDateRange,
  selectedWebinars, setSelectedWebinars,
  customStartDate, setCustomStartDate,
  customEndDate, setCustomEndDate,
  showFilterDropdown, setShowFilterDropdown,
  // Search / sort state
  searchTerm, setSearchTerm,
  sortBy, setSortBy,
  sortOrder, setSortOrder,
  // Data
  data,
  // Heatmap
  heatmapEnabled, onHeatmapToggle,
  scalingType, onScalingTypeChange,
}) => {
  const activeFilters =
    filterType === 'dateRange' && dateRange !== 'all' ? 1
    : filterType === 'webinars' ? selectedWebinars.size
    : 0;

  const clearFilters = () => {
    setFilterType('all');
    setDateRange('all');
    setSelectedWebinars(new Set());
    setCustomStartDate('');
    setCustomEndDate('');
    setShowFilterDropdown(false);
  };

  const toggleWebinar = (id) => {
    const next = new Set(selectedWebinars);
    next.has(id.toString()) ? next.delete(id.toString()) : next.add(id.toString());
    setSelectedWebinars(next);
  };

  const filterLabel = () => {
    if (filterType === 'all') return 'All data';
    if (filterType === 'dateRange') {
      const map = { '7days': 'Last 7d', '30days': 'Last 30d', '90days': 'Last 90d', 'custom': 'Custom' };
      return map[dateRange] || 'Date range';
    }
    if (filterType === 'webinars') return `${selectedWebinars.size} webinar${selectedWebinars.size !== 1 ? 's' : ''}`;
    return 'All data';
  };

  return (
    <div className="px-4 py-3 md:px-6 bg-grey-50 border-b border-grey-200 flex flex-wrap items-center gap-2">

      {/* ── Search ──────────────────────────────────────────────────────── */}
      <div className="relative flex-shrink-0">
        <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-grey-400" />
        <input
          type="text"
          placeholder="Search campaigns..."
          value={searchTerm || ''}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-8 pr-3 py-1.5 border border-grey-300 rounded-lg text-xs text-darkblue-500 bg-white w-48 focus:outline-none focus:ring-2 focus:ring-brightblue-300 focus:border-brightblue-400 transition-colors"
        />
      </div>

      <div className="w-px h-4 bg-grey-300 flex-shrink-0" />

      {/* ── Filter dropdown ─────────────────────────────────────────────── */}
      <div className="relative flex-shrink-0">
        <button
          onClick={() => setShowFilterDropdown(!showFilterDropdown)}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
            activeFilters > 0
              ? 'bg-darkblue-500 text-white border-darkblue-500'
              : 'bg-white text-darkblue-500 border-grey-300 hover:border-darkblue-200'
          }`}
        >
          {filterLabel()}
          <ChevronDownIcon className="w-3 h-3" />
        </button>

        {showFilterDropdown && (
          <>
            <div className="fixed inset-0 z-20" onClick={() => setShowFilterDropdown(false)} />
            <div className="absolute top-full left-0 mt-1 w-72 bg-white border border-grey-200 rounded-xl shadow-lg z-30 overflow-hidden">
              <div className="px-4 py-2.5 bg-grey-50 border-b border-grey-200">
                <span className="text-xs font-semibold text-darkblue-500">Filter campaigns</span>
              </div>
              <div className="p-3 space-y-2.5">
                {/* All */}
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="tcFilter" checked={filterType === 'all'} onChange={clearFilters} className="text-brightblue-500" />
                  <span className="text-xs text-darkblue-500">All data</span>
                </label>

                {/* Date range */}
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="tcFilter" checked={filterType === 'dateRange'} onChange={() => setFilterType('dateRange')} className="text-brightblue-500" />
                  <span className="text-xs text-darkblue-500">Date range</span>
                </label>
                {filterType === 'dateRange' && (
                  <div className="ml-5 space-y-1.5">
                    {[['7days','Last 7 days'],['30days','Last 30 days'],['90days','Last 90 days'],['custom','Custom range']].map(([val, lbl]) => (
                      <label key={val} className="flex items-center gap-2 cursor-pointer">
                        <input type="radio" name="tcDateRange" checked={dateRange === val} onChange={() => setDateRange(val)} className="text-brightblue-500" />
                        <span className="text-xs text-darkblue-500">{lbl}</span>
                      </label>
                    ))}
                    {dateRange === 'custom' && (
                      <div className="ml-5 space-y-1.5 mt-1">
                        <input type="date" value={customStartDate} onChange={(e) => setCustomStartDate(e.target.value)}
                          className="w-full px-2 py-1 border border-grey-300 rounded-lg text-xs text-darkblue-500 focus:outline-none focus:ring-1 focus:ring-brightblue-300" />
                        <input type="date" value={customEndDate} min={customStartDate || undefined} onChange={(e) => setCustomEndDate(e.target.value)}
                          className="w-full px-2 py-1 border border-grey-300 rounded-lg text-xs text-darkblue-500 focus:outline-none focus:ring-1 focus:ring-brightblue-300" />
                      </div>
                    )}
                  </div>
                )}

                {/* Webinars */}
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" name="tcFilter" checked={filterType === 'webinars'} onChange={() => setFilterType('webinars')} className="text-brightblue-500" />
                  <span className="text-xs text-darkblue-500">Specific webinars</span>
                </label>
                {filterType === 'webinars' && (
                  <div className="ml-5 max-h-36 overflow-y-auto space-y-1">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-xs text-grey-500">{selectedWebinars.size} selected</span>
                      <button onClick={() => setSelectedWebinars(new Set())} className="text-xs text-brightblue-600 font-medium">Clear</button>
                    </div>
                    {data.map((item) => (
                      <label key={item.id} className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={selectedWebinars.has(item.id.toString())} onChange={() => toggleWebinar(item.id)}
                          className="rounded border-grey-300 text-brightblue-500" />
                        <span className="text-xs text-darkblue-500">Campaign {item.id} <span className="text-grey-400">({new Date(item.date).toLocaleDateString()})</span></span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <div className="px-4 py-2.5 bg-grey-50 border-t border-grey-200 flex gap-2">
                <button onClick={clearFilters} className="flex-1 px-3 py-1.5 border border-grey-300 rounded-lg text-xs font-semibold text-grey-600 hover:bg-grey-100 transition-colors">Reset</button>
                <button onClick={() => setShowFilterDropdown(false)} className="flex-1 px-3 py-1.5 bg-brand-500 text-white rounded-lg text-xs font-semibold hover:bg-brand-600 transition-colors">Apply</button>
              </div>
            </div>
          </>
        )}
      </div>

      {activeFilters > 0 && (
        <button onClick={clearFilters} className="p-1 rounded text-grey-400 hover:text-pink-600 transition-colors flex-shrink-0" title="Clear filters">
          <XMarkIcon className="w-3.5 h-3.5" />
        </button>
      )}

      <div className="w-px h-4 bg-grey-300 flex-shrink-0" />

      {/* ── Sort ────────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-1.5 flex-shrink-0">
        <span className="text-xs text-grey-400 font-semibold">Sort</span>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="px-2 py-1.5 border border-grey-300 rounded-lg text-xs text-darkblue-500 bg-white focus:outline-none focus:ring-2 focus:ring-brightblue-300"
        >
          <option value="date">Date</option>
          <option value="registrations">Registrations</option>
          <option value="attendees">Attendees</option>
          <option value="bookings">Bookings</option>
        </select>
        <select
          value={sortOrder}
          onChange={(e) => setSortOrder(e.target.value)}
          className="px-2 py-1.5 border border-grey-300 rounded-lg text-xs text-darkblue-500 bg-white focus:outline-none focus:ring-2 focus:ring-brightblue-300"
        >
          <option value="desc">High → Low</option>
          <option value="asc">Low → High</option>
        </select>
      </div>

      {/* ── Heatmap controls ─────────────────────────────────────────────── */}
      <div className="ml-auto flex items-center gap-2 flex-shrink-0">
        <button
          onClick={onHeatmapToggle}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
            heatmapEnabled
              ? 'bg-teal-50 border-teal-200 text-teal-700'
              : 'bg-white border-grey-300 text-darkblue-500 hover:border-darkblue-200'
          }`}
        >
          <SwatchIcon className="w-3.5 h-3.5" />
          {heatmapEnabled ? 'Heatmap on' : 'Heatmap off'}
        </button>

        {heatmapEnabled && (
          <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-white border border-grey-300 rounded-lg flex-shrink-0">
            <Cog6ToothIcon className="w-3.5 h-3.5 text-grey-400 flex-shrink-0" />
            <select
              value={scalingType}
              onChange={(e) => onScalingTypeChange(e.target.value)}
              className="text-xs text-darkblue-500 border-none bg-transparent focus:outline-none focus:ring-0"
            >
              <option value={SCALING_TYPES.COLUMN_WISE}>Column-wise</option>
              <option value={SCALING_TYPES.TABLE_WIDE}>Table-wide</option>
              <option value={SCALING_TYPES.FIXED_RANGE}>Fixed range</option>
            </select>
          </div>
        )}
      </div>
    </div>
  );
};

export default TableControls;